import { Link, useParams } from 'react-router-dom';
import { motion } from 'motion/react';
import { Badge } from 'src/components/ui/badge';
import { CopyButton } from 'src/components/ui/copy-button';
import { useAttributionForAddress } from 'src/hooks/attributions/useAttributionForAddress';
import { truncateText } from 'src/utils/text';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from 'src/components/ui/tooltip';
import { LockedAttributionHoverCard } from 'src/components/Attribution/LockedAttributionHoverCard';
import { isLockedAttribution } from 'src/components/Address/utils';

interface TxIORowProps {
  address: string;
  amount: string;
  shouldAnimate?: boolean;
  hasAdvancedAccess?: boolean;
}

const getBadgeLabel = (text: string, maxLength: number = 20) => {
  if (text.length <= maxLength) {
    return { label: text, isTruncated: false };
  }
  return {
    label: `${text.slice(0, maxLength)}...`,
    isTruncated: true,
  };
};

export const TxIORow = ({
  address,
  amount,
  shouldAnimate = true,
  hasAdvancedAccess = false,
}: TxIORowProps) => {
  const { id: currentAddress } = useParams<{ id: string }>();

  const { attribution } = useAttributionForAddress('bitcoin', address);

  const clusterId = attribution?.cluster_id;
  const scriptType = attribution?.address_type;
  const entity = attribution?.entity;
  const isCurrentAddress = address === currentAddress;
  const isLink = !isCurrentAddress;

  const renderLabel = () => {
    // Helper function to conditionally truncate address based on available space
    const getDisplayAddress = (addr: string) => {
      return hasAdvancedAccess ? truncateText(addr) : addr;
    };

    // Scenario 3: Entity exists and is NOT locked - show badge + address
    if (entity && !isLockedAttribution(entity)) {
      const { label: badgeLabel, isTruncated } = getBadgeLabel(entity);

      const badgeElement = (
        <Badge variant={isCurrentAddress ? 'outline' : 'primary'}>
          {badgeLabel}
        </Badge>
      );

      const content = (
        <div className="group flex h-6 w-full items-center">
          <div className="attribution-label">
            <Tooltip>
              <TooltipTrigger>
                {isTruncated ? (
                  <Tooltip>
                    <TooltipTrigger>{badgeElement}</TooltipTrigger>
                    <TooltipContent>
                      <p>{entity}</p>
                    </TooltipContent>
                  </Tooltip>
                ) : (
                  badgeElement
                )}
              </TooltipTrigger>
              <TooltipContent>
                <p>{address}</p>
              </TooltipContent>
            </Tooltip>
          </div>
          <div className="raw-address">
            <Tooltip>
              <TooltipTrigger>
                <span
                  className={`${isCurrentAddress ? 'text-black' : 'text-amber-600'}`}
                >
                  {getDisplayAddress(address)}
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <p>{address}</p>
              </TooltipContent>
            </Tooltip>
          </div>
          <CopyButton
            text={address}
            iconSize={16}
            className="ml-1 opacity-0 transition-opacity group-hover:opacity-100"
          />
        </div>
      );

      return isLink ? (
        <Link
          to={`/bitcoin/address/${address}`}
          className="cursor-pointer hover:opacity-80"
        >
          {content}
        </Link>
      ) : (
        content
      );
    }

    // Scenario 1 & 2: No entity OR locked entity - show just address
    // If locked, also show lock icon
    const addressButton = (
      <span
        className={`${isCurrentAddress ? 'text-black' : isLink ? 'cursor-pointer text-amber-600' : 'text-black'}`}
      >
        {getDisplayAddress(address)}
      </span>
    );

    const addressContent = isLink ? (
      <Link
        to={`/bitcoin/address/${address}`}
        className="cursor-pointer hover:opacity-80"
      >
        <Tooltip>
          <TooltipTrigger>{addressButton}</TooltipTrigger>
          <TooltipContent>
            <p>{address}</p>
          </TooltipContent>
        </Tooltip>
      </Link>
    ) : (
      <Tooltip>
        <TooltipTrigger>{addressButton}</TooltipTrigger>
        <TooltipContent>
          <p>{address}</p>
        </TooltipContent>
      </Tooltip>
    );

    return (
      <div className="group flex h-6 w-full items-center gap-1">
        {addressContent}
        {entity && isLockedAttribution(entity) && (
          <LockedAttributionHoverCard
            attributionValue={entity}
            attributionType="entity"
            iconSize="sm"
            triggerClassName="ml-1"
            fallback={null}
          />
        )}
        <CopyButton
          text={address}
          iconSize={16}
          className="ml-1 opacity-0 transition-opacity group-hover:opacity-100"
        />
      </div>
    );
  };

  return (
    <motion.tr
      className="bg-gray-50 text-sm hover:bg-gray-100"
      initial={shouldAnimate ? 'hidden' : false}
      animate={shouldAnimate ? 'visible' : false}
      variants={
        shouldAnimate
          ? {
              hidden: {
                opacity: 0,
                scale: 0.96,
              },
              visible: {
                opacity: 1,
                scale: 1,
              },
            }
          : undefined
      }
      transition={
        shouldAnimate
          ? {
              duration: 0.15,
              ease: 'easeOut',
            }
          : undefined
      }
      layout={shouldAnimate}
    >
      <td className="p-3">{renderLabel()}</td>
      {hasAdvancedAccess && (
        <td className="p-3">
          {clusterId ? (
            <div className="group flex h-6 w-full items-center">
              <Link
                to={`/bitcoin/address/${clusterId}`}
                className="cursor-pointer text-amber-600 hover:opacity-80"
              >
                <Tooltip>
                  <TooltipTrigger>{truncateText(clusterId)}</TooltipTrigger>
                  <TooltipContent>
                    <p>{clusterId}</p>
                  </TooltipContent>
                </Tooltip>
              </Link>
              <CopyButton
                text={clusterId}
                iconSize={16}
                className="ml-1 opacity-0 transition-opacity group-hover:opacity-100"
              />
            </div>
          ) : (
            <></>
          )}
        </td>
      )}
      {hasAdvancedAccess && (
        <td className="truncate p-3 text-muted-foreground">{scriptType}</td>
      )}
      <td className="truncate p-3 text-right text-foreground">{amount}</td>
    </motion.tr>
  );
};
